import TrendyStatistic from "./TrendyStatistic.js";
import Statistic from "./Statistic.js";
import {TimeDelta} from "../time.js";
import KPI from "./KPI.js";

export class CategoricalKPI {

    // Data Types
    static NUMBER = "number";
    static CURRENCY = "currency";
    static PERCENT = "percent";
    static PERCENTILE = "percentile";

    constructor({
        title, operation = Statistic.AVERAGE, dataType = CategoricalKPI.NUMBER,
        categories=[], chartType="pie", currentWindow=null, previousWindow=null
    }) {
        this.title = title;
        this.operation = operation;
        this.dataType = dataType;
        this.categories = categories; // Array of category names
        this.categoryStats = {}; // Object to hold (trendy) statistics for each category
        this.chartType = chartType;
        this.currentWindow = currentWindow;
        this.previousWindow = previousWindow;

        categories.forEach(category => {
            this.categoryStats[category] = new TrendyStatistic({
                title: `${category}`,
                operation: operation,
                dataType: dataType,
                currentWindow,
                previousWindow
            });
        });
    }

    add({value, date, category, isSample = true}) {
        if (this.categoryStats[category] === undefined) {
            this.categoryStats[category] = new TrendyStatistic({
                title: `${category}`,
                operation: this.operation,
                dataType: this.dataType,
                currentWindow: this.currentWindow,
                previousWindow: this.previousWindow
            })
        }
        this.categoryStats[category].add({value, date, isSample});
    }

    value() {
        return Object.entries(this.categoryStats).reduce((acc, [category, stats]) => {
            acc[category] = stats.value();
            return acc;
        }, {});
    }

    chartDatasets({binSizeDays}) {
        return Object.entries(this.categoryStats).reduce((acc, [category, trendyStat]) => {
            const cds = trendyStat.chartDatasets({
                binSizeDays,
                sampleChartType: null,
                excludeUniverse: true,
                splitByWindow: true
            })

            const {previous, current} = cds;
            const previousCategoryValue = previous.datasets[0].data.reduce((acc, point) => {
                const [x, y] = point;
                return acc + y;
            }, 0)
            const currentCategoryValue = current.datasets[0].data.reduce((acc, point) => {
                const [x, y] = point;
                return acc + y;
            }, 0)
            acc[category] = {
                previous: previousCategoryValue,
                current: currentCategoryValue
            };
            return acc;
        }, {});
    }

    trend() {
        const trends = {};
        Object.keys(this.categoryStats).forEach(category => {
            trends[category] = this.categoryStats[category].trend();
        });
        return trends;
    }

    chartPlugins() {
        return {
            legend: {
                display: true,
                position: "top",
            },
            title: {
                display: true,
                text: this.title
            }
        };
    }

    chartOptions(labels) { //, datasets, extraData) {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: this.chartPlugins(),
            scales: {
                x: {
                    title: {
                        display: false,
                    },
                    display: false,
                },
                y: {
                    title: {
                        display: false,
                    },
                    display: false,
                }
            }
        };
    }

    format(value) {
        if (isNaN(value)) {
            return null;
        }
        if (this.dataType === KPI.NUMBER) {
            // Add commas and stuff:
            if (value === undefined || value === null) {
                return "N/A";
            }
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value);
        } else if (this.dataType === KPI.CURRENCY) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        } else if (this.dataType === KPI.PERCENT) {
            return new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value);
        } else if (this.dataType === KPI.PERCENTILE) {
            // Assuming value is a percentile score, format it as a percentage
            return "Top " + (new Intl.NumberFormat('en-US', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).format(value)) + " of all products";
        }
        return value;
    }

    chart({
              binSizeDays
          }) {

        const datasetsByCategory = this.chartDatasets({binSizeDays})

        const {previous, current} = Object.values(datasetsByCategory).reduce((arr, categoryData) => {
            arr.previous.push(categoryData.previous);
            arr.current.push(categoryData.current);
            return arr;
        }, {previous: [], current: []});

        const labels = Object.keys(datasetsByCategory);

        return {
            type: this.chartType,
            options: this.chartOptions(labels), // , previousDatasets, extraData),
            data: {
                labels: labels,
                datasets: [
                    {
                        label: TimeDelta.formatShortDate(this.previousWindow.start) + " - " + TimeDelta.formatShortDate(this.previousWindow.end),
                        data: previous
                    },
                    {
                        label: TimeDelta.formatShortDate(this.currentWindow.start) + " - " + TimeDelta.formatShortDate(this.currentWindow.end),
                        data: current
                    }
                ]
            }
        }
    }

    chartKey() {
        return "categorical-kpi-chart-" + this.title;
    }
}


export default CategoricalKPI;